import { Button, CircularProgress, Divider, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { useRef, useState } from 'react';
import { FileUploader as SystemFileUploader } from 'react-drag-drop-files';
import { useTranslation } from 'react-i18next';
import Backend, { FileInfo } from '../backend/Backend';
import AquantCancelIcon from '../resources/images/aq-cancel.svg';
import FileUploadDeleteIcon from '../resources/images/import_delete_lconfig.svg';
import { ReactComponent as InfoIcon } from '../resources/images/info-icon.svg';
import StorageFileType from '../storage/UserRoleType';
import { ConfigStore } from '../stores/ConfigStore';
import './FileUploader.scss';
import StorageExplorer from './StorageExplorer';

function FilesPopover(props: { config_id: any; folderType: string }) {
    const { config_id, folderType } = props;
    const { oktaAuth } = useOktaAuth();
    const buttonRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [fileList, setFileList] = useState<any>([]);
    const [loading, setLoading] = useState(true);

    const open = Boolean(anchorEl);

    const handleClick = async () => {
        setAnchorEl(buttonRef.current);
        const files = await Backend.getFilesByFolderType(config_id, folderType, oktaAuth);
        setFileList(files);
        setLoading(false);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setFileList([]);
        setLoading(true);
    };

    return (
        <div>
            <Button onClick={handleClick} ref={buttonRef} variant="text" color="primary" data-testId="seeAllFiles">
                See all files
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    style: {
                        boxShadow: '0px 0px 10px rgba(196, 196, 196, 0.25)',
                        padding: '20px',
                    },
                }}
            >
                <Stack width="529px" overflow="hidden">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom="10px">
                        <Typography fontSize="16px" fontWeight="500">
                            List of files previously uploaded
                        </Typography>
                        <img
                            src={AquantCancelIcon}
                            onClick={() => {
                                handleClose();
                            }}
                            alt="cancel icon"
                            style={{ cursor: 'pointer' }}
                        />
                    </Stack>
                    {loading ? (
                        <Stack width="100%" alignItems="center">
                            <CircularProgress data-testid="CircularProgress" size={20} color="primary" />
                        </Stack>
                    ) : null}
                    {!loading && fileList?.length === 0 && (
                        <Typography fontWeight="300" fontSize="13px" color="#3B3B3B">
                            No files found
                        </Typography>
                    )}
                    {!loading && fileList?.length > 0 && (
                        <Stack maxHeight="220px" overflow="auto" padding="0">
                            <ul style={{ paddingLeft: 20 }}>
                                {fileList
                                    ? fileList.map((file, index) => (
                                          <li key={index}>
                                              <Stack
                                                  display="flex"
                                                  flexDirection="row"
                                                  marginBottom={index < fileList.length - 1 && '15px'}
                                              >
                                                  <Typography
                                                      fontWeight="300"
                                                      fontSize="13px"
                                                      color="#3B3B3B"
                                                      marginRight="5px"
                                                  >
                                                      {file.name}
                                                  </Typography>
                                                  <Typography fontWeight="300" fontSize="13px" color="#999CA1">
                                                      ({(file.size / 1024.0 / 1024.0).toFixed()} MB |{' '}
                                                      {new Date(file.last_modified_at).toLocaleDateString()})
                                                  </Typography>
                                              </Stack>
                                          </li>
                                      ))
                                    : null}
                            </ul>
                        </Stack>
                    )}
                </Stack>
            </Popover>
        </div>
    );
}

interface FileUploaderProps {
    name: string;
    title: string;
    tooltipText: String;
    onFilesSelected: Function;
    onFileRemoved: Function;
    files: Array<File>;
    isRequired?: boolean;
    fromStorage: boolean;
    progress: Function;
    configStore: ConfigStore;
}

const fileTypes = ['CSV'];

const FileUploader = (props: FileUploaderProps) => {
    const { name, title, tooltipText, files, isRequired, fromStorage } = props;
    const { t } = useTranslation();

    const handleFilesSelected = (files: Array<File>) => {
        props.onFilesSelected(files);
    };

    const handleOnRemoveClicked = (file: File, index: number) => {
        props.onFileRemoved(file, index);
    };

    const handleStorageFileSelected = (path: string, f: FileInfo) => {
        const files = [];

        let fname = f.name;
        if (path.length > 0) {
            fname = `${path}/${fname}`;
        }

        // mimic File since this is only reference to actual file on storge
        files.push({ name: fname, size: f.size });
        props.onFilesSelected(files);
    };

    return (
        <Stack data-testId="uploadFileContainer" direction="column" className="FileUploader" spacing={2}>
            <Stack
                data-testId="uploadFileTitleContainer"
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row">
                    <Typography data-testId="uploadFileContainerTitle" className="Title">
                        {' '}
                        {title}{' '}
                    </Typography>
                    {isRequired ? (
                        <Typography sx={{ color: '#ED4D4D', fontWeight: '500', fontSize: '16px', marginLeft: '5px' }}>
                            {' '}
                            *{' '}
                        </Typography>
                    ) : null}
                    <Tooltip sx={{ padding: '0px', marginLeft: '12px' }} title={tooltipText}>
                        <IconButton>
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
                <FilesPopover
                    config_id={props.configStore.parent_config_id}
                    folderType={name === 'masterFiles' ? 'master' : 'lookup'}
                />
            </Stack>
            {fromStorage ? (
                <StorageExplorer
                    allowedfilterType={StorageFileType.csv}
                    {...props}
                    compact
                    handleStorageFileSelected={handleStorageFileSelected}
                />
            ) : null}
            {!fromStorage && (
                <SystemFileUploader
                    multiple
                    handleChange={handleFilesSelected}
                    name={name}
                    types={fileTypes}
                    fileOrFiles={files && files.length > 0 ? files : null}
                    classes="SystemFileUploader"
                >
                    <div className="Title" data-testid={`data-source-${props.name}-box`}>
                        <div>{t('import.drag-drop-help1')}</div>
                        <div>{t('import.drag-drop-help2')}</div>
                    </div>
                </SystemFileUploader>
            )}
            <ul className="FileList">
                {files?.map((mfile, index) => (
                    <li key={`${mfile.name}-${index}`}>
                        <div data-testid={`FileListItem[${index}]`} className="FileListItem">
                            <div>{mfile.name}</div>
                            <Divider orientation="vertical" className="Separator" />
                            <div>{(mfile.size / 1024.0 / 1024.0).toFixed(2)} MB</div>
                            <Divider orientation="vertical" className="Separator" />
                            <div className="RemoveFile" onClick={() => handleOnRemoveClicked(mfile, index)}>
                                <img alt="" src={FileUploadDeleteIcon} className="DeleteIcon" />
                                <div>{t('import.remove-file')}</div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </Stack>
    );
};

export default FileUploader;
